import { supabase } from './supabase';

/* ─── Types ─── */

export type CustomFieldType = 'text' | 'number' | 'date' | 'checkbox' | 'select';

export interface CustomFieldDefinition {
  id: string;
  name: string;
  type: CustomFieldType;
  options?: string[]; // Opções quando type === 'select'
}

export type CustomFieldValues = Record<string, any>;

export type BoardVisibility = 'org' | 'private';

export interface OrgPerson {
  user_id: string;
  full_name: string | null;
  role: string;
}

export interface KanbanBoard {
  id: string;
  org_id: string;
  title: string;
  description: string | null;
  visibility: BoardVisibility;
  custom_fields?: CustomFieldDefinition[] | null;
  created_by: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface KanbanBoardWithCounts extends KanbanBoard {
  card_count: number;
  column_count: number;
}

export interface KanbanColumn {
  id: string;
  board_id: string;
  title: string;
  position: number;
  color: string | null;
  created_at: string | null;
}

export interface KanbanCard {
  id: string;
  column_id: string;
  title: string;
  description: string | null;
  position: number;
  media_asset_ids: string[] | null;
  custom_field_values?: CustomFieldValues | null;
  assignee_ids: string[];
  created_by: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface BoardData {
  board: KanbanBoard;
  columns: (KanbanColumn & { cards: KanbanCard[] })[];
}

export const KANBAN_LIMITS = {
  title: 200,
  description: 4000,
  fieldName: 80,
  fieldOption: 80,
  fieldCount: 40,
  optionsPerField: 30,
} as const;

const FIELD_TYPES: CustomFieldType[] = ['text', 'number', 'date', 'checkbox', 'select'];
const BLOCKED_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const SAFE_ID = /^[a-zA-Z0-9_-]{4,64}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function stripControls(value: string, allowNewline: boolean): string {
  let out = '';
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code === 9) {
      out += ' ';
      continue;
    }
    if (allowNewline && (code === 10 || code === 13)) {
      out += code === 13 ? '' : '\n';
      continue;
    }
    if (code < 32 || code === 127) continue;
    out += value[i];
  }
  return out;
}

export function sanitizePlainText(value: string, max: number, allowNewline = false): string {
  return stripControls(String(value ?? ''), allowNewline).trim().slice(0, max);
}

export function isCustomFieldType(value: string): value is CustomFieldType {
  return FIELD_TYPES.indexOf(value as CustomFieldType) !== -1;
}

export function newCustomFieldId(): string {
  return crypto.randomUUID();
}

export function sanitizeFieldId(id: string): string | null {
  const raw = String(id || '').trim();
  if (BLOCKED_KEYS.has(raw) || !SAFE_ID.test(raw)) return null;
  return raw;
}

export function sanitizeVisibility(value: unknown): BoardVisibility {
  return value === 'private' ? 'private' : 'org';
}

export function sanitizeUuidList(ids: string[] | undefined | null, allowed?: Set<string>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  (ids || []).forEach(id => {
    const raw = String(id || '').trim();
    if (!UUID.test(raw) || seen.has(raw)) return;
    if (allowed && !allowed.has(raw)) return;
    seen.add(raw);
    out.push(raw);
  });
  return out;
}

export function sanitizeCustomFields(fields: CustomFieldDefinition[] | null | undefined): CustomFieldDefinition[] {
  const seen = new Set<string>();
  const out: CustomFieldDefinition[] = [];
  (fields || []).forEach(field => {
    if (out.length >= KANBAN_LIMITS.fieldCount) return;
    const id = sanitizeFieldId(field?.id);
    const name = sanitizePlainText(field?.name || '', KANBAN_LIMITS.fieldName);
    if (!id || !name || seen.has(id) || !isCustomFieldType(field?.type)) return;
    seen.add(id);
    const next: CustomFieldDefinition = { id, name, type: field.type };
    if (field.type === 'select') {
      const options = (field.options || [])
        .map(o => sanitizePlainText(String(o), KANBAN_LIMITS.fieldOption))
        .filter(Boolean)
        .slice(0, KANBAN_LIMITS.optionsPerField);
      if (options.length === 0) return;
      next.options = options;
    }
    out.push(next);
  });
  return out;
}

export function sanitizeCustomFieldValues(
  values: CustomFieldValues | null | undefined,
  fields?: CustomFieldDefinition[] | null,
): CustomFieldValues {
  const src = values && typeof values === 'object' && !Array.isArray(values) ? values : {};
  const out: CustomFieldValues = Object.create(null);
  const known = fields ? new Map(fields.map(f => [f.id, f])) : null;
  Object.keys(src).forEach(key => {
    const id = sanitizeFieldId(key);
    if (!id) return;
    const field = known?.get(id);
    const raw = (src as Record<string, unknown>)[key];
    if (field?.type === 'checkbox') {
      out[id] = raw === true;
      return;
    }
    if (field?.type === 'number') {
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(n)) out[id] = n;
      return;
    }
    if (field?.type === 'date') {
      const d = sanitizePlainText(String(raw ?? ''), 10);
      if (DATE_ONLY.test(d)) out[id] = d;
      return;
    }
    if (field?.type === 'select') {
      const v = sanitizePlainText(String(raw ?? ''), KANBAN_LIMITS.fieldOption);
      if (v && (field.options || []).indexOf(v) !== -1) out[id] = v;
      return;
    }
    const text = sanitizePlainText(String(raw ?? ''), 500, false);
    if (text) out[id] = text;
  });
  return out;
}

/* ─── Boards ─── */

export async function fetchBoards(orgId: string): Promise<KanbanBoard[]> {
  const { data, error } = await supabase
    .from('kanban_boards')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as KanbanBoard[];
}

export async function fetchBoardsWithCounts(orgId: string): Promise<KanbanBoardWithCounts[]> {
  // Fetch boards
  const { data: boards, error: bErr } = await supabase
    .from('kanban_boards')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });
  if (bErr) throw bErr;
  if (!boards || boards.length === 0) return [];

  // Fetch all columns for these boards
  const boardIds = boards.map(b => b.id);
  const { data: columns, error: cErr } = await supabase
    .from('kanban_columns')
    .select('id, board_id')
    .in('board_id', boardIds);
  if (cErr) throw cErr;

  // Fetch all cards for these columns
  const colIds = (columns || []).map(c => c.id);
  let cardCounts: Record<string, number> = {};
  if (colIds.length > 0) {
    const { data: cards, error: cardErr } = await supabase
      .from('kanban_cards')
      .select('id, column_id')
      .in('column_id', colIds);
    if (cardErr) throw cardErr;

    // Build column -> board mapping
    const colToBoardMap: Record<string, string> = {};
    (columns || []).forEach(c => { colToBoardMap[c.id] = c.board_id; });

    // Count cards per board
    (cards || []).forEach(card => {
      const boardId = colToBoardMap[card.column_id];
      if (boardId) cardCounts[boardId] = (cardCounts[boardId] || 0) + 1;
    });
  }

  // Count columns per board
  const colCounts: Record<string, number> = {};
  (columns || []).forEach(c => {
    colCounts[c.board_id] = (colCounts[c.board_id] || 0) + 1;
  });

  return boards.map(b => ({
    ...(b as KanbanBoard),
    card_count: cardCounts[b.id] || 0,
    column_count: colCounts[b.id] || 0,
  }));
}

export async function fetchOrgDirectory(orgId: string): Promise<OrgPerson[]> {
  const { data, error } = await supabase.rpc('list_org_directory', { p_org_id: orgId });
  if (error) throw error;
  return (data || []) as OrgPerson[];
}

export async function createBoard(
  orgId: string,
  title: string,
  createdBy: string,
  description?: string,
  visibility: BoardVisibility = 'org',
): Promise<KanbanBoard> {
  const { data, error } = await supabase
    .from('kanban_boards')
    .insert([{
      org_id: orgId,
      title: sanitizePlainText(title, KANBAN_LIMITS.title),
      description: description ? sanitizePlainText(description, KANBAN_LIMITS.description, true) : null,
      created_by: createdBy,
      visibility: sanitizeVisibility(visibility),
    }])
    .select()
    .single();
  if (error) throw error;

  // Create default columns
  const defaults = [
    { board_id: data.id, title: 'A Fazer', position: 0, color: '#6366f1' },
    { board_id: data.id, title: 'Em Progresso', position: 1, color: '#f59e0b' },
    { board_id: data.id, title: 'Concluído', position: 2, color: '#10b981' },
  ];
  const { error: colError } = await supabase.from('kanban_columns').insert(defaults);
  if (colError) throw colError;

  return data as KanbanBoard;
}

export async function updateBoard(
  boardId: string,
  updates: { title?: string; description?: string | null; custom_fields?: CustomFieldDefinition[] | null; visibility?: BoardVisibility },
): Promise<void> {
  const next: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.title !== undefined) next.title = sanitizePlainText(updates.title, KANBAN_LIMITS.title);
  if (updates.description !== undefined) {
    next.description = updates.description
      ? sanitizePlainText(updates.description, KANBAN_LIMITS.description, true)
      : null;
  }
  if (updates.custom_fields !== undefined) next.custom_fields = sanitizeCustomFields(updates.custom_fields);
  if (updates.visibility !== undefined) next.visibility = sanitizeVisibility(updates.visibility);
  const { error } = await supabase
    .from('kanban_boards')
    .update(next)
    .eq('id', boardId);
  if (error) throw error;
}

export async function deleteBoard(boardId: string): Promise<void> {
  const { error } = await supabase.from('kanban_boards').delete().eq('id', boardId);
  if (error) throw error;
}

/* ─── Board Data (columns + cards) ─── */

export async function fetchBoardData(boardId: string): Promise<BoardData | null> {
  const { data: board, error: bErr } = await supabase
    .from('kanban_boards')
    .select('*')
    .eq('id', boardId)
    .single();
  if (bErr) throw bErr;
  if (!board) return null;

  const { data: columns, error: cErr } = await supabase
    .from('kanban_columns')
    .select('*')
    .eq('board_id', boardId)
    .order('position', { ascending: true });
  if (cErr) throw cErr;

  const colIds = (columns || []).map((c) => c.id);
  let cards: KanbanCard[] = [];
  if (colIds.length > 0) {
    const { data: cardData, error: cardErr } = await supabase
      .from('kanban_cards')
      .select('*')
      .in('column_id', colIds)
      .order('position', { ascending: true });
    if (cardErr) throw cardErr;
    cards = (cardData || []) as KanbanCard[];
  }

  return {
    board: board as KanbanBoard,
    columns: (columns || []).map((col) => ({
      ...(col as KanbanColumn),
      cards: cards.filter((card) => card.column_id === col.id),
    })),
  };
}

/* ─── Columns ─── */

export async function createColumn(
  boardId: string,
  title: string,
  position: number,
  color?: string,
): Promise<KanbanColumn> {
  const { data, error } = await supabase
    .from('kanban_columns')
    .insert([{ board_id: boardId, title: sanitizePlainText(title, KANBAN_LIMITS.title), position, color: color || '#6366f1' }])
    .select()
    .single();
  if (error) throw error;
  return data as KanbanColumn;
}

export async function updateColumn(
  columnId: string,
  updates: { title?: string; position?: number; color?: string },
): Promise<void> {
  const { error } = await supabase
    .from('kanban_columns')
    .update({
      ...updates,
      ...(updates.title !== undefined ? { title: sanitizePlainText(updates.title, KANBAN_LIMITS.title) } : {}),
    })
    .eq('id', columnId);
  if (error) throw error;
}

export async function deleteColumn(columnId: string): Promise<void> {
  const { error } = await supabase.from('kanban_columns').delete().eq('id', columnId);
  if (error) throw error;
}

/** Batch-update column positions */
export async function reorderColumns(columnIds: string[]): Promise<void> {
  const updates = columnIds.map((id, index) => ({ id, position: index }));
  for (const u of updates) {
    const { error } = await supabase
      .from('kanban_columns')
      .update({ position: u.position })
      .eq('id', u.id);
    if (error) throw error;
  }
}

/* ─── Cards ─── */

export async function createCard(
  columnId: string,
  title: string,
  createdBy: string,
  options?: {
    description?: string;
    position?: number;
    media_asset_ids?: string[];
    custom_field_values?: CustomFieldValues | null;
    assignee_ids?: string[];
    fields?: CustomFieldDefinition[] | null;
    allowedAssignees?: Set<string>;
    allowedMedia?: Set<string>;
  },
): Promise<KanbanCard> {
  const { data, error } = await supabase
    .from('kanban_cards')
    .insert([{
      column_id: columnId,
      title: sanitizePlainText(title, KANBAN_LIMITS.title),
      created_by: createdBy,
      description: options?.description ? sanitizePlainText(options.description, KANBAN_LIMITS.description, true) : null,
      position: options?.position ?? 0,
      media_asset_ids: sanitizeUuidList(options?.media_asset_ids, options?.allowedMedia),
      custom_field_values: sanitizeCustomFieldValues(options?.custom_field_values, options?.fields),
      assignee_ids: sanitizeUuidList(options?.assignee_ids, options?.allowedAssignees),
    }])
    .select()
    .single();
  if (error) throw error;
  return data as KanbanCard;
}

export async function updateCard(
  cardId: string,
  updates: {
    title?: string;
    description?: string;
    position?: number;
    column_id?: string;
    media_asset_ids?: string[];
    custom_field_values?: CustomFieldValues | null;
    assignee_ids?: string[];
    fields?: CustomFieldDefinition[] | null;
    allowedAssignees?: Set<string>;
    allowedMedia?: Set<string>;
  },
): Promise<void> {
  const next: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.title !== undefined) next.title = sanitizePlainText(updates.title, KANBAN_LIMITS.title);
  if (updates.description !== undefined) {
    next.description = updates.description
      ? sanitizePlainText(updates.description, KANBAN_LIMITS.description, true)
      : null;
  }
  if (updates.position !== undefined) next.position = updates.position;
  if (updates.column_id !== undefined) next.column_id = updates.column_id;
  if (updates.media_asset_ids !== undefined) next.media_asset_ids = sanitizeUuidList(updates.media_asset_ids, updates.allowedMedia);
  if (updates.custom_field_values !== undefined) next.custom_field_values = sanitizeCustomFieldValues(updates.custom_field_values, updates.fields);
  if (updates.assignee_ids !== undefined) next.assignee_ids = sanitizeUuidList(updates.assignee_ids, updates.allowedAssignees);
  const { error } = await supabase
    .from('kanban_cards')
    .update(next)
    .eq('id', cardId);
  if (error) throw error;
}

export async function deleteCard(cardId: string): Promise<void> {
  const { error } = await supabase.from('kanban_cards').delete().eq('id', cardId);
  if (error) throw error;
}

export async function moveCard(
  cardId: string,
  newColumnId: string,
  newPosition: number,
): Promise<void> {
  const { error } = await supabase
    .from('kanban_cards')
    .update({ column_id: newColumnId, position: newPosition, updated_at: new Date().toISOString() })
    .eq('id', cardId);
  if (error) throw error;
}

/** Batch-update card positions within a column */
export async function reorderCards(cardIds: string[]): Promise<void> {
  for (let i = 0; i < cardIds.length; i++) {
    const { error } = await supabase
      .from('kanban_cards')
      .update({ position: i, updated_at: new Date().toISOString() })
      .eq('id', cardIds[i]);
    if (error) throw error;
  }
}
